import {
  Container,
  Box,
  Grid,
  SpaceBetween,
  Button,
  GridProps,
  Modal,
  Link,
  Header,
  BarChart,
  Spinner,
  StatusIndicator,
} from '@cloudscape-design/components';

import { SMSMessage, PlaceContactBase } from '../common/types';
import { formatName } from '../common/names';

export type ChatTurnProps = {
  index: number;
  message: SMSMessage;
  sendSuggestion?: (uuid: string, type?: 'session' | 'message') => void;
  loading?: boolean;
  suggestionType?: 'session' | 'message';
  hideSuggestions?: boolean;
  contact?: PlaceContactBase;
};

export function ChatTurn(props: ChatTurnProps) {
  const message = props.message;
  const index = props.index;

  const author = (
    <Box variant="small">
      {/* TODO put user's name here */}
      {message.author === 'client'
        ? ([props.contact?.first_name, props.contact?.last_name].filter((x) => x).join(' ') ??
          'Client')
        : message.author === 'assistant'
          ? (message.user?.name ?? 'Assistant')
          : (message.user?.name ?? 'User')}
      {message.created_at ? ` | ${new Date(message.created_at + 'Z').toLocaleString()}` : ''}
    </Box>
  );

  // return (
  //   <Container
  //     key={`turn-${message.uuid}`}
  //     className={message.author === 'client' ? 'chat-turn-user' : 'chat-turn-assistant-text'}
  //     disableHeaderPaddings
  //   >
  //     {author}
  //     {content}
  //   </Container>
  // );
  const createdAt = message.created_at
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short', // Show abbreviated month
        day: 'numeric', // Show day
        // year: 'numeric', // Show year
        hour: 'numeric', // Show hour
        minute: 'numeric', // Show minute
        hour12: true, // 12-hour format
      }).format(new Date(message.created_at + 'Z'))
    : '';

  let textColor = 'text-black';
  let authorColor = 'text-black';
  let createdAtColor = 'text-gray-800';
  let color = 'bg-gray-200';
  let authorName =
    props.contact?.first_name || props.contact?.last_name
      ? formatName(props.contact.first_name, props.contact.last_name)
      : 'Client';
  if (message.user) {
    color = 'bg-[#2f80ed]';
    textColor = 'text-white';
    authorColor = 'text-white';
    createdAtColor = 'text-white';
    authorName = message.user.name;
  }

  let content = null;

  const messageText = message.message;

  const text = messageText
    .trim()
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .map((line, line_idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <p key={`turn-${index}-line-${line_idx}`} className={`${textColor}`}>
        {line.trim()}
      </p>
    ));

  if (message.suggestions && props.sendSuggestion && !props.hideSuggestions) {
    const options = message.suggestions.map((option, option_idx) => (
      <Button
        key={option.uuid}
        className="chat-option-button"
        disabled={props.loading}
        variant="normal" //'normal' | 'primary' | 'link' | 'icon' | 'inline-icon' | 'inline-link'
        fullWidth
        onClick={() => {
          if (props.sendSuggestion && !props.loading) {
            props.sendSuggestion(option.uuid, props.suggestionType);
          }
        }}
      >
        <Box variant="p">{option.message}</Box>
      </Button>
    ));
    let gridDefinition: ReadonlyArray<GridProps.ElementDefinition> = [];

    if (options.length === 1) {
      gridDefinition = [{ colspan: { default: 12, xs: 6 } }];
    } else if (options.length === 2) {
      gridDefinition = [{ colspan: { default: 12, xs: 6 } }, { colspan: { default: 12, xs: 6 } }];
    } else if (options.length === 3) {
      gridDefinition = [
        { colspan: { default: 12, xs: 4 } },
        { colspan: { default: 12, xs: 4 } },
        { colspan: { default: 12, xs: 4 } },
      ];
    } else if (options.length === 4) {
      gridDefinition = [
        { colspan: { default: 12, xs: 6 } },
        { colspan: { default: 12, xs: 6 } },
        { colspan: { default: 12, xs: 6 } },
        { colspan: { default: 12, xs: 6 } },
      ];
    } else {
      gridDefinition = options.map(() => ({ colspan: { default: 12, xs: 6 } }));
    }

    content = (
      <SpaceBetween direction="vertical" size="xs">
        {text}
        <Grid gridDefinition={gridDefinition}>{options}</Grid>
      </SpaceBetween>
    );
  } else {
    content = (
      <SpaceBetween direction="vertical" size="xs">
        {text}
      </SpaceBetween>
    );
  }

  if (!message.user) {
    return (
      <div
        key={index}
        className={`message flex flex-col max-w-auto break-words ${color} ${textColor} self-start rounded-tl-lg rounded-tr-lg rounded-br-lg px-2 mr-2 py-2 max-w-[90%]`}
      >
        <div className="flex flex-col mb-1">
          <p className={`text-sm ${authorColor} font-bold`}>{authorName}</p>
          <p className={`text-xs ${createdAtColor}`}>{createdAt}</p>
        </div>
        {content}
      </div>
    );
  }

  return (
    <div
      key={index}
      className={`message flex flex-col max-w-auto break-words ${color} ${textColor} self-end rounded-tl-lg rounded-bl-lg rounded-tr-lg custom-overflow-wrap px-4 mr-4 py-2 max-w-[90%]`}
    >
      <div className="flex flex-col mb-1">
        <p className={`text-sm ${authorColor} font-bold`}>{authorName}</p>
        <p className={`text-xs ${createdAtColor}`}>{createdAt}</p>
      </div>
      {content}
    </div>
  );
}
